import {useEffect, useMemo, useState} from 'react'
import language from '../../variables/language';
import '../../css/form.css'
import '../../css/student-card.css'
import Selector from '../../components/forms/Selector';
import TimezoneSelect from '../../components/forms/TimezoneSelect';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import IconSave from '../../components/icons/IconSave';
import StudentsConversation from './StudentsConversations';

const TEXT_FIELDS = [
   {name:'name', type:'text'},
   {name:'surname', type:'text'},
   {name:'email', type:'text'},
   {name:'telegramUsername', type:'text'},
];
const SETTINGS_SELECTORS_WIDE = [
   {name:'responsibleId', url:'/api/users'},
   {name:'project', url:'/api/projects'},
   {name:'groupe', url:'/api/groups'},
   {name:'timeZone', url:'/api/timezones'},
];
const SETTINGS_SELECTORS_NARROW = [
   {name:'level', url:'/api/levels', narrow:true},
   {name:'isActive', url:'/api/booleans', narrow:true},
];
const PRICE_FIELDS = [
   {name:'priceIndividual', type:'number', narrow:true},
   {name:'priceGroup', type:'number', narrow:true},
];
const RESOURCE_FIELDS = [
   {name:'gMeetId', type:'text'},
   {name:'gdocument', type:'text'},
   {name:'gDictionaryUa', type:'text'},
   {name:'gDictionaryEng', type:'text'},
   {name:'schedule', type:'text'},
   {name:'ocupation', type:'text'},
   {name:'location', type:'text'},
];
const BROADCAST_SELECTOR = {name:'hasBroadcast', url:'/api/booleans'};
const ALL_FIELDS = [...TEXT_FIELDS, ...SETTINGS_SELECTORS_WIDE, ...SETTINGS_SELECTORS_NARROW, ...PRICE_FIELDS, ...RESOURCE_FIELDS, BROADCAST_SELECTOR];

const resolveValue = (val) => {
   if(val && typeof val === 'object' && val.id !== undefined) {
      return String(val.id);
   }
   // some legacy student columns store the literal string "null" instead of
   // a real NULL - treat it the same as unset.
   if(val === null || val === undefined || val === 'null') {
      return '';
   }
   return String(val);
}

const flattenStudentData = (data) => {
   if(!data || !data.id) {
      return null;
   }
   const flat = {};
   ALL_FIELDS.forEach(f => flat[f.name] = resolveValue(data[f.name]));
   return flat;
}

export default function FormStudent(props) {
   const navigate = useNavigate();
   const [formValues, setFormValues] = useState(null);
   const [initialValues, setInitialValues] = useState(null);
   const [saving, setSaving] = useState(false);

   useEffect(() => {
      const flat = flattenStudentData(props.data);
      if(!flat) {
         return;
      }
      setFormValues(flat);
      setInitialValues(flat);
   }, [props.data]);

   const isDirty = useMemo(() => {
      if(!formValues || !initialValues) {
         return false;
      }
      return JSON.stringify(formValues) !== JSON.stringify(initialValues);
   }, [formValues, initialValues]);

   const handleFieldChange = (name, value) => {
      setFormValues(prev => ({...prev, [name]: value}));
   }

   const handleDiscard = () => {
      setFormValues(initialValues);
   }

   const handleSave = () => {
      if(!isDirty || saving) {
         return;
      }
      setSaving(true);
      // PUT /api/students/:id binds a full Student entity, but every field is
      // null-guarded in StudentService.update() before being applied, so
      // omitting unset/empty fields behaves as a safe partial update.
      // That's exactly why clearing a previously-set optional field (e.g.
      // timeZone) can't go through this same PUT - an omitted key and an
      // explicitly-cleared one look identical ("don't touch"), so there's
      // no way to represent "the user wants this unset". The dedicated
      // DELETE below is the one exception that bypasses that ambiguity.
      const payload = {};
      Object.keys(formValues).forEach(key => {
         if(formValues[key] !== '') {
            payload[key] = formValues[key];
         }
      });
      const requests = [
         axios.put(props.url, JSON.stringify(payload), {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
         }),
      ];
      if (formValues.timeZone === '' && initialValues.timeZone !== '') {
         requests.push(axios.delete(props.url + '/timezone', { withCredentials: true }));
      }
      Promise.all(requests).then(() => {
         props.onRefreshHundler();
      }).catch(err => {
         if(err.response && err.response.status === 401){
            navigate('/login');
         }
         console.log(err);
      }).finally(() => {
         setSaving(false);
      });
   }

   if(!formValues || !initialValues) {
      return <div></div>;
   }

   const fieldClass = (f) => 'flex-from-input-wraper ' + (f.narrow ? 'student-card-field-sm' : 'student-card-field');

   const renderField = (f) => (
      <div className={fieldClass(f)} data-label={language[language.lang][f.name]} key={f.name}>
         <input className='submit-field from-input txt-c' type={f.type}
            placeholder={language[language.lang][f.name]}
            value={formValues[f.name]}
            onChange={(e) => handleFieldChange(f.name, e.target.value)} />
      </div>
   );

   const renderSelector = (f) => (
      <div className={fieldClass(f)} data-label={language[language.lang][f.name]} key={f.name}>
         {f.name === 'timeZone' ? (
            <TimezoneSelect
               name={f.name} url={f.url} value={formValues[f.name]} disabled={false}
               selectorsHundler={(value) => handleFieldChange(f.name, value)} />
         ) : (
            <Selector className='submit-field form-selection txt-c'
               name={f.name} url={f.url} value={formValues[f.name]} disabled={false}
               selectorsHundler={(value) => handleFieldChange(f.name, value)} />
         )}
      </div>
   );

   return (
      <div className='student-card-body'>
         <div className='student-card-form-col'>
            <form className='student-card-section' onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
               <p className='student-card-section-title'>{language[language.lang].student}</p>
               <div className='student-card-fields-grid'>
                  {TEXT_FIELDS.map(renderField)}
               </div>
            </form>

            <div className='student-card-section'>
               <p className='student-card-section-title'>{language[language.lang].settings}</p>
               <div className='student-card-fields-grid'>
                  {SETTINGS_SELECTORS_WIDE.map(renderSelector)}
               </div>
               <div className='student-card-fields-grid'>
                  {SETTINGS_SELECTORS_NARROW.map(renderSelector)}
                  {PRICE_FIELDS.map(renderField)}
               </div>
            </div>

            <div className='student-card-section'>
               <p className='student-card-section-title'>{language[language.lang].resources}</p>
               <div className='student-card-fields-grid'>
                  {RESOURCE_FIELDS.map(renderField)}
                  {renderSelector(BROADCAST_SELECTOR)}
               </div>
            </div>

            {isDirty ? (
               <div className='student-card-form-footer'>
                  <span className='student-card-unsaved-hint'>{language[language.lang].unsavedChanges}</span>
                  <div className='student-card-discard-link' onClick={handleDiscard}>{language[language.lang].cancel}</div>
                  <button type='button' className='student-card-save-btn' disabled={saving} onClick={handleSave}>
                     <IconSave />
                     {saving ? language[language.lang]['Loading ...'] : language[language.lang].save}
                  </button>
               </div>
            ) : ''}
         </div>

         <div className='student-card-conv-col'>
            <div className='student-card-conv-panel'>
               <StudentsConversation id={props.data.id}
                  studentLabel={(props.data.name || '') + ' ' + (props.data.surname || '')} />
            </div>
         </div>
      </div>
   )
}
